import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ToastContext } from './toastContext.js'

const DEFAULT_DURATION_MS = 4500

/**
 * Shared app toast surface.
 *
 * Accepts either a string or:
 * { title, message, variant, duration, actionLabel, onAction }
 */
export default function ToastProvider({ children }) {
  const [toast, setToast] = useState(null)

  // Fire once per toast id — do not reset timer when `dismissing` updates.
  useEffect(() => {
    if (!toast || toast.dismissing) return
    const ms =
      typeof toast.duration === 'number' ? toast.duration : DEFAULT_DURATION_MS
    const tid = window.setTimeout(() => {
      setToast((t) => (t && !t.dismissing ? { ...t, dismissing: true } : t))
    }, ms)
    return () => clearTimeout(tid)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reschedule when a new toast is shown
  }, [toast?.id])

  useEffect(() => {
    if (!toast?.dismissing) return
    const tid = window.setTimeout(() => setToast(null), 200)
    return () => clearTimeout(tid)
  }, [toast?.dismissing, toast?.id])

  const showToast = useCallback((payload) => {
    const message = typeof payload === 'string' ? payload : payload?.message
    const title = typeof payload === 'string' ? payload : (payload?.title ?? message)
    if (!title && !message) return
    const variant =
      typeof payload === 'string' ? 'success' : (payload?.variant ?? 'success')
    const duration =
      typeof payload === 'string'
        ? DEFAULT_DURATION_MS
        : (payload?.duration ?? DEFAULT_DURATION_MS)
    setToast({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      title,
      message: title === message ? null : message,
      variant,
      duration,
      actionLabel: typeof payload === 'string' ? null : payload?.actionLabel,
      onAction: typeof payload === 'string' ? null : payload?.onAction,
      dismissing: false,
    })
  }, [])

  const requestDismiss = useCallback(() => {
    setToast((t) => (t && !t.dismissing ? { ...t, dismissing: true } : t))
  }, [])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast &&
        createPortal(
          <div className="app-toast-anchor">
            <div
              className={`app-toast app-toast--${toast.variant} toast${toast.dismissing ? ' dismissing' : ''}`}
              role={toast.variant === 'error' ? 'alert' : 'status'}
              aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
            >
              <span className="app-toast__icon" aria-hidden="true">
                {toast.variant === 'error' ? '!' : toast.variant === 'info' ? '✦' : '✓'}
              </span>
              <div className="app-toast__body">
                <div className="app-toast__title">{toast.title}</div>
                {toast.message && <div className="app-toast__text">{toast.message}</div>}
              </div>
              {toast.actionLabel && (
                <button
                  type="button"
                  className="app-toast__action"
                  onClick={() => {
                    toast.onAction?.()
                    requestDismiss()
                  }}
                >
                  {toast.actionLabel}
                </button>
              )}
              <button
                type="button"
                className="app-toast__dismiss"
                aria-label="Dismiss notification"
                onClick={requestDismiss}
              >
                ×
              </button>
            </div>
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  )
}
